import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BACKEND_BASE_URL } from "../../config.js";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableSortLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

function formatDurationSeconds(totalSeconds) {
  if (totalSeconds == null || Number.isNaN(Number(totalSeconds))) return "—";
  const secs = Math.max(0, Math.round(Number(totalSeconds)));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  if (mins < 60) return rem ? `${mins}m ${rem}s` : `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins ? `${hours}h ${remMins}m` : `${hours}h`;
}

const statusColor = {
  SUCCESS: "success",
  FAILURE: "error",
  NO_SUBTITLES: "warning",
  STARTED: "warning",
  RETRY: "warning",
};

const scraperStatusColor = {
  Running: "success",
  Idle: "default",
  Error: "error",
};

const headerCellSx = {
  backgroundColor: "#152F8F",
  color: "#fff",
  fontWeight: "bold",
  border: "1px solid #ddd",
};

const sortLabelSx = {
  color: "white !important",
  "&:hover": { color: "yellow !important" },
  "&.Mui-active": { color: "white !important" },
  "& .MuiTableSortLabel-icon": { color: "white !important", opacity: 1 },
  "&:hover .MuiTableSortLabel-icon": { color: "yellow !important" },
  "&.Mui-active .MuiTableSortLabel-icon": { color: "yellow !important" },
};

function formatDetail(log) {
  const summary = log.result_summary;

  if (log.display_status === "NO_SUBTITLES") {
    return (
      log.error ||
      (summary && (summary.error || summary.message)) ||
      "Subtitles are disabled for this video"
    );
  }

  if (log.status === "FAILURE" || log.display_status === "FAILURE") {
    const reason =
      log.error ||
      (summary && (summary.error || summary.message)) ||
      "";
    return reason || "—";
  }

  if (summary && typeof summary === "object") {
    const chars = summary.char_count != null ? `${summary.char_count} chars` : "";
    const message = summary.message || log.message || "";
    return [chars, message].filter(Boolean).join(" · ") || "—";
  }

  if (log.traceback) {
    const lines = String(log.traceback)
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    const causeIdx = lines.findIndex((l) => l.includes("most likely caused by"));
    if (causeIdx >= 0) {
      const next = lines
        .slice(causeIdx + 1)
        .find((l) => l && !l.startsWith("If you are sure"));
      if (next) return next;
    }
    return lines[lines.length - 1] || "See traceback";
  }

  return "—";
}

function DetailCell({ text }) {
  const value = text || "—";
  return (
    <Tooltip title={value} placement="top-start" arrow enterDelay={300}>
      <Box
        sx={{
          maxWidth: "420px",
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: 1.4,
        }}
      >
        {value}
      </Box>
    </Tooltip>
  );
}

function getSortValue(log, key) {
  switch (key) {
    case "video_id":
      return (log.video_id || log.result_summary?.video_id || "").toLowerCase();
    case "municipality":
      return (log.municipality || "").toLowerCase();
    case "county":
      return (log.county || "").toLowerCase();
    case "state":
      return (log.state || "").toLowerCase();
    case "status":
      return (log.display_status || log.status || "").toLowerCase();
    case "date_done":
      return log.date_done ? new Date(log.date_done).getTime() : 0;
    case "detail":
      return formatDetail(log).toLowerCase();
    default:
      return "";
  }
}

function statusLabel(log) {
  if (log.display_status === "NO_SUBTITLES") {
    return "No subtitles";
  }
  return log.display_status || log.status || "—";
}

export default function CeleryLogsTable() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [counts, setCounts] = useState({
    total: 0,
    success: 0,
    failure: 0,
    no_subtitles: 0,
  });
  const [scraperStatus, setScraperStatus] = useState({
    status: "Idle",
    last_start_time: null,
    error: null,
    today_scrape_group: null,
    next_scrape_group: null,
    avg_celery_task_duration_seconds: null,
    avg_celery_task_sample_count: 0,
  });
  const [retentionDays, setRetentionDays] = useState(7);
  const [sortConfig, setSortConfig] = useState({
    key: "date_done",
    direction: "desc",
  });

  const fetchLogs = async (
    nextPage = page,
    nextRowsPerPage = rowsPerPage,
    nextSearch = searchQuery,
    nextStatus = statusFilter
  ) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/api/celery-logs/`, {
        params: {
          page: nextPage + 1,
          page_size: nextRowsPerPage,
          q: nextSearch,
          status: nextStatus,
        },
      });
      setLogs(response.data?.results || []);
      setTotalItems(response.data?.count || 0);
      setCounts(
        response.data?.counts || {
          total: 0,
          success: 0,
          failure: 0,
          no_subtitles: 0,
        }
      );
      if (response.data?.scraper_status) {
        setScraperStatus(response.data.scraper_status);
      }
      if (response.data?.retention_days) {
        setRetentionDays(response.data.retention_days);
      }
    } catch (error) {
      console.error("Error fetching scrape/transcript logs:", error);
      setLogs([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLogs(0, rowsPerPage, searchQuery, statusFilter);
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, rowsPerPage]);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    fetchLogs(newPage, rowsPerPage, searchQuery, statusFilter);
  };

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedLogs = useMemo(() => {
    const rows = [...logs];
    const { key, direction } = sortConfig;
    rows.sort((a, b) => {
      const av = getSortValue(a, key);
      const bv = getSortValue(b, key);
      if (av < bv) return direction === "asc" ? -1 : 1;
      if (av > bv) return direction === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }, [logs, sortConfig]);

  const columns = [
    { key: "video_id", label: "Video ID" },
    { key: "municipality", label: "Municipality" },
    { key: "county", label: "County" },
    { key: "state", label: "State" },
    { key: "status", label: "Status" },
    { key: "date_done", label: "Date Done" },
    { key: "detail", label: "Detail" },
  ];

  const lastStartLabel = scraperStatus.last_start_time
    ? new Date(scraperStatus.last_start_time).toLocaleString()
    : "—";

  const scraperChip = (
    <Chip
      size="small"
      label={`Scraper status: ${scraperStatus.status}`}
      color={scraperStatusColor[scraperStatus.status] || "default"}
      icon={
        scraperStatus.status === "Error" ? (
          <ErrorOutlineIcon fontSize="small" />
        ) : undefined
      }
    />
  );

  // Keep scraper status current while this page is open
  useEffect(() => {
    const timer = setInterval(() => {
      axios
        .get(`${BACKEND_BASE_URL}/api/scraper-status/`)
        .then((response) => {
          if (response.data) setScraperStatus(response.data);
        })
        .catch(() => {});
    }, 15000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
        <Typography style={{ fontWeight: "bold" }} variant="h4">
          Scrape & Transcript Logs
        </Typography>
        <Box display="flex" alignItems="center" flexWrap="nowrap">
          <TextField
            placeholder="Search video, town, error..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ marginRight: "10px", minWidth: "240px", backgroundColor: "#fff" }}
            InputProps={{
              startAdornment: (
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  style={{ marginRight: "8px", color: "#555" }}
                />
              ),
            }}
          />
          <FormControl
            size="small"
            style={{ minWidth: "150px", marginRight: "10px", backgroundColor: "#fff" }}
          >
            <InputLabel id="celery-status-filter-label">Status</InputLabel>
            <Select
              labelId="celery-status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failure">Scrape failure</MenuItem>
              <MenuItem value="no_subtitles">No subtitles</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            style={{ backgroundColor: "#0B194A", color: "white", whiteSpace: "nowrap" }}
            onClick={() => fetchLogs(page, rowsPerPage, searchQuery, statusFilter)}
            disabled={loading}
            startIcon={<RefreshIcon />}
          >
            Refresh
          </Button>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box
          display="flex"
          gap={2}
          marginBottom={2}
          flexWrap="wrap"
          alignItems="center"
          sx={{
            p: 1.5,
            border: "1px solid #ddd",
            borderRadius: "4px",
            backgroundColor: "#f9fafc",
          }}
        >
          {scraperStatus.status === "Error" && scraperStatus.error ? (
            <Tooltip title={scraperStatus.error} arrow placement="top">
              {scraperChip}
            </Tooltip>
          ) : (
            scraperChip
          )}
          <Typography variant="body2" color="textSecondary">
            Last start time: <strong>{lastStartLabel}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Today&apos;s scrape group:{" "}
            <strong>{scraperStatus.today_scrape_group ?? "—"}</strong>
            {" · "}
            Next: <strong>{scraperStatus.next_scrape_group ?? "—"}</strong>
            <span style={{ color: "#777" }}> (1→2→3 cycle · ~3:00 AM EST)</span>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Avg celery task completion:{" "}
            <strong>
              {formatDurationSeconds(
                scraperStatus.avg_celery_task_duration_seconds
              )}
            </strong>
            {scraperStatus.avg_celery_task_sample_count
              ? ` (${scraperStatus.avg_celery_task_sample_count} weekly scrape runs)`
              : ""}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ ml: "auto" }}>
            Showing last {retentionDays} days (rolling)
          </Typography>
        </Box>

        <Box display="flex" gap={1} marginBottom={2} flexWrap="wrap">
          <Chip label={`Total: ${counts.total || 0}`} />
          <Chip label={`Success: ${counts.success || 0}`} color="success" />
          <Chip label={`Failure: ${counts.failure || 0}`} color="error" />
          <Chip
            label={`No subtitles: ${counts.no_subtitles || 0}`}
            color="warning"
            variant={statusFilter === "no_subtitles" ? "filled" : "outlined"}
            onClick={() => setStatusFilter("no_subtitles")}
            sx={{ cursor: "pointer" }}
          />
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : sortedLogs.length === 0 ? (
          <Typography variant="h6">No transcript jobs logged in the last {retentionDays} days.</Typography>
        ) : (
          <Box sx={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "4px" }}>
            <Table size="small" style={{ minWidth: "1100px", backgroundColor: "#fff" }}>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.key} sx={headerCellSx}>
                      <TableSortLabel
                        active={sortConfig.key === col.key}
                        direction={sortConfig.key === col.key ? sortConfig.direction : "asc"}
                        onClick={() => handleSort(col.key)}
                        sx={sortLabelSx}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedLogs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell style={{ border: "1px solid #ddd" }}>
                      {log.video_id || log.result_summary?.video_id || "—"}
                    </TableCell>
                    <TableCell style={{ border: "1px solid #ddd" }}>
                      {log.municipality || "—"}
                    </TableCell>
                    <TableCell style={{ border: "1px solid #ddd" }}>
                      {log.county || "—"}
                    </TableCell>
                    <TableCell style={{ border: "1px solid #ddd" }}>
                      {log.state || "—"}
                    </TableCell>
                    <TableCell style={{ border: "1px solid #ddd" }}>
                      <Chip
                        size="small"
                        label={statusLabel(log)}
                        color={
                          statusColor[log.display_status || log.status] || "default"
                        }
                      />
                    </TableCell>
                    <TableCell style={{ border: "1px solid #ddd" }}>
                      {log.date_done ? new Date(log.date_done).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell style={{ border: "1px solid #ddd" }}>
                      <DetailCell text={formatDetail(log)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
